/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Subscription} from 'rxjs';
import {ChangeDetectionStrategy, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';

import {deferSignalStateWrite} from '../util/template-state';
import template from './performance-chart.component.html';

import {Downloader} from '../../../components/util/downloader';
import {StatsApiService} from '../controller/stats-api.service';
import {CHART_COLORS} from '../options/chart-colors';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_Y_AXIS_OPTIONS, ALT_IDS} from '../options/chart-options';
import {getAvailableFields} from './chart-options/chart-options-fields';
import {ObjectTypeFinder} from '../constants/object-type';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {AppLocaleService} from '../locale/app-locale.service';
import {getIntlLocaleData} from '../locale/intl-locale-data';
import {applyHighchartsLocale} from '../../workspace/widget/workspace-widget-chart-commons';
import {UserProfileApiService} from '../../admin/user-profile/user-profile-api.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-performance-chart',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformanceChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() obj: any;
  @Input() subObj: any;
  @Input() adnChartType: string;
  @Input() chartType: string;
  @Input() idKey: string;
  @Input() useAlt: boolean;
  @Input() absChunkMin: string;
  @Input() groupBy: string;
  @Input() groupById: string;
  @Input() showCollapsed: any;
  @Input() dateSelectorId: string;
  @Input() currency: any;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  isCollection = false;
  expanded = false;
  isMarketplace = false;
  dateOption: any;
  startDate: any;
  endDate: any;
  countType = ADN_CHART_COUNT_TYPES.singular;
  yAxisOption = ADN_CHART_Y_AXIS_OPTIONS.shared;
  selFieldIds: any[] = [];
  selFieldId: any;
  selectedCurrency: any;
  readonly haveStats = signal(false);
  readonly error = signal<any>(false);
  readonly isPending = signal(false);
  readonly noData = signal(false);
  showAudit = false;
  readonly totals = signal<any>(undefined);
  auditHook: any;

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;
  private series: any[] = [];
  private chartOptionsConfig: any;
  private localUser: any;
  private profile: any;
  private networkTimezoneOffset = 0;
  private networkCurrency: any;
  private previousStats: any = {};
  private statsSuccessCallback: (stats: any, theSeries?: any[]) => void;
  private initialized = false;
  private statsSubscription?: Subscription;
  private requestVersion = 0;
  private destroyed = false;

  get resolvedChartType(): string {
    return this.adnChartType || this.chartType;
  }

  get resolvedSubObj(): any {
    return this.subObj;
  }

  get resolvedIdKey(): string {
    return this.idKey;
  }

  get resolvedUseAlt(): boolean {
    return this.useAlt;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get showCollapsedCoerced(): boolean {
    const showCollapsed = this.showCollapsed;
    return showCollapsed === true || showCollapsed === 'true';
  }

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    private readonly ogTranslate: OgTranslateService,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
  ) {
  }

  ngOnInit(): void {
    this.initProfileAndChart();
    this.initialized = true;
    if (this.chartOptionsConfig) {
      this.startStatsGet();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    if (changes.adnChartType || changes.chartType) {
      this.initProfileAndChart();
      if (this.chartOptionsConfig) {
        this.startStatsGet();
      }
      return;
    }

    if (changes.currency) {
      this.selectedCurrency = this.currency;
    }

    if (changes.obj || changes.subObj || changes.currency) {
      this.isCollection = !_.get(this.obj, 'id');
      this.series = this.getSelFields();
      if (this.chartOptionsConfig) {
        this.startStatsGet();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requestVersion++;
    this.statsSubscription?.unsubscribe();
    this.destroyChart();
  }

  changeCallback(
    dateOptionId: string,
    startDate: any,
    endDate: any,
    countTypeId: string,
    statTypeId: string,
    yAxisOptionId: string,
    selFieldIds: any,
    selFieldId: any,
    selCurrency: any,
    showAudit: any
  ): void {
    void statTypeId;

    this.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[dateOptionId];
    this.startDate = startDate;
    this.endDate = endDate;
    this.countType = ADN_CHART_COUNT_TYPES[countTypeId];
    this.selFieldIds = selFieldIds;
    this.selFieldId = selFieldId;
    this.selectedCurrency = selCurrency;
    this.yAxisOption = ADN_CHART_Y_AXIS_OPTIONS[yAxisOptionId];
    this.series = this.getSelFields();
    this.showAudit = showAudit;
    this.startStatsGet();

    const perfKey = this.chartOptionsConfig.perfChartOptions;
    this.profile[perfKey] = _.assign({}, this.profile[perfKey], {
      dateOptionId: _.get(this.dateOption, 'id'),
      startDate: moment(this.startDate).format(),
      endDate: moment(this.endDate).format(),
      yAxisOptionId: this.yAxisOption.id,
      countTypeId: this.countType.id,
      selFieldIds: this.selFieldIds,
      selFieldId: this.selFieldId
    });

    firstValueFrom(this.UserProfile.saveProfile({
      id: this.localUser.userId,
      profile: _.pick(this.profile, perfKey)
    })).then((apiUser: any) => {
      this.localUser = this.localUserProfile.safeSaveProfile(perfKey, apiUser.profile[perfKey]);
      this.profile = this.localUser.profile;
    });
  }

  save(allFields?: boolean, format = 'xlsx', totalsOnly?: boolean): void {
    void format;

    const selectableFields = _.map(getAvailableFields(this.ChartOptionsUtil, this.localUserProfile, this.resolvedChartType, this.location), (f: any) => {
      return f.altId || f.id;
    });
    const allTotals = _.cloneDeep(this.previousStats.stats.totals);
    const allDataFields = _.filter(this.getSelFields(allFields), (f: any) => {
      return this.resolvedSubObj || ((selectableFields.indexOf(f.altId) || selectableFields.indexOf(f.id) > -1) && !_.isNil(allTotals[f.altId || f.id]));
    });
    const fields = _.map(allDataFields, (adf: any) => {
      return adf.altId || adf.id;
    });

    const rows: any[] = [];
    if (!totalsOnly) {
      const refData = _.cloneDeep(this.series[0].data);
      this.statsSuccessCallback(this.previousStats.stats, allDataFields);
      for (let i = 0; i < refData.length; i++) {
        const dateData: any = {};
        for (let j = 0; j < allDataFields.length; j++) {
          if (j === 0) {
            dateData.date = allDataFields[j].data[i].x;
          }
          dateData[allDataFields[j].id] = allDataFields[j].data[i].y;
        }
        rows.push(dateData);
      }
    }

    const totals: any = _.pick(allTotals, selectableFields);
    _.forEach(allDataFields, (f: any) => {
      if (f.isPercentage) {
        totals[f.id] = totals[f.id] * 100;
      }
    });
    totals.date = 'TOTALS';
    rows.push(totals);

    const headingFields = _.map(allDataFields, 'name');
    headingFields.unshift(totalsOnly ? 'Totals' : 'Date');
    fields.unshift('date');

    if (rows.length > 0 || totalsOnly) {
      const keysLength = _.keys(rows[0]).length;
      if (headingFields.length !== keysLength || fields.length !== keysLength) {
        _.forEach(_.keys(rows[0]), (k: string) => {
          if (fields.indexOf(k) < 0 && fields.length !== keysLength) {
            fields.push(k);
            totals[k] = allTotals[k];
          }
          if (headingFields.indexOf(k) < 0 && headingFields.length !== keysLength) {
            headingFields.push(k);
          }
        });
      }
    }

    const date = this.dateOption ? this.translate.instant('chart.dateOption.' + this.dateOption.id) : 'custom dates';
    const objectType = ObjectTypeFinder.findByCaps(this.resolvedChartType);
    const defaultName = ((this.isCollection || this.resolvedIdKey ? ('All ' + (objectType ? objectType.name : '')) : this.obj.name) + ' ' + date + '.xls').replace(/ /g, '-');
    new Downloader().saveXls2(defaultName, headingFields, fields, rows, allDataFields, true);
  }

  private initProfileAndChart(): void {
    if (!this.resolvedChartType) {
      return;
    }

    this.isCollection = !_.get(this.obj, 'id');
    this.expanded = false;
    this.isMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();

    this.chartOptionsConfig = this.ChartOptionsUtil.getOptions(this.resolvedChartType, this.isMarketplace);
    if (!this.chartOptionsConfig) {
      return;
    }
    this.localUser = this.localUserProfile.get();
    this.profile = this.localUser.profile;

    const perfKey = this.chartOptionsConfig.perfChartOptions;
    const profileStartDate = _.get(this.profile[perfKey], 'startDate');
    const profileEndDate = _.get(this.profile[perfKey], 'endDate');
    const profileCountType = ADN_CHART_COUNT_TYPES[_.get(this.profile[perfKey], 'countTypeId')];
    const profileYAxisOption = ADN_CHART_Y_AXIS_OPTIONS[_.get(this.profile[perfKey], 'yAxisOptionId')];
    const profileSelFieldIds = _.get(this.profile[perfKey], 'selFieldIds');
    const profileSelFieldId = _.get(this.profile[perfKey], 'selFieldId');

    this.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[_.get(this.profile[perfKey], 'dateOptionId')];
    if (!this.dateOption && (!profileStartDate || !profileEndDate)) {
      this.dateOption = this.ChartOptionsUtil.getDateOptions().last12Hours;
    }
    this.startDate = this.dateOption ? this.dateOption.startDate() : moment(profileStartDate);
    this.endDate = this.dateOption ? this.dateOption.endDate() : moment(profileEndDate);
    this.countType = profileCountType ? profileCountType : ADN_CHART_COUNT_TYPES.singular;
    this.yAxisOption = profileYAxisOption ? profileYAxisOption : ADN_CHART_Y_AXIS_OPTIONS.shared;
    this.selFieldIds = profileSelFieldIds ? profileSelFieldIds : [_.find(_.map(this.chartOptionsConfig.fields, 'id'), (id: string) => id === 'rendered')];
    this.selFieldId = profileSelFieldId ? profileSelFieldId : _.find(_.map(this.chartOptionsConfig.fields, 'id'), (id: string) => id === 'rendered');

    const timezone = this.LocalNetworkProfile.get('timeZone');
    this.networkCurrency = this.LocalNetworkProfile.get('defaultCurrency');
    this.networkTimezoneOffset = moment.tz(timezone).utcOffset();

    this.selectedCurrency = this.currency;
    this.series = this.getSelFields();
    this.chartOptions = this.createChartConfig('line', this.series, this.getYAxes(), 280);
  }

  private fieldTranslateFunc = (m: any) => {
    m.name = this.ogTranslate.transform('chart.statType.' + (m.key || m.tKey || m.id), m.name);
    return m;
  };

  private getSelFields(allFields?: boolean): any[] {
    if (!this.isCollection && !this.resolvedSubObj) {
      const map = _.map(_.filter(_.cloneDeep(this.chartOptionsConfig.fields), (value: any) => {
        return (allFields || this.selFieldIds.indexOf(value.id) > -1) && value.id !== 'customFields';
      }), this.fieldTranslateFunc);
      if (this.yAxisOption === ADN_CHART_Y_AXIS_OPTIONS.shared) {
        _.forEach(map, (c: any) => {
          c.yAxis = 0;
        });
      }
      return map;
    }
    return _.map(_.map(this.resolvedSubObj || this.obj, (c: any) => {
      return {
        data: [],
        id: c.id,
        name: c.name,
        yAxis: 0
      };
    }), this.fieldTranslateFunc);
  }

  private createChartConfig(type: string, series: any[], yAxisData: any, height: number): any {
    return {
      chart: {
        type,
        zoomType: 'x',
        showAxes: true,
        height
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false
          },
          turboThreshold: 2000
        }
      },
      tooltip: {
        shared: true
      },
      xAxis: {
        type: 'datetime',
        title: {
          enabled: false,
          text: 'Date and Time'
        }
      },
      yAxis: yAxisData,
      series,
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      exporting: {
        enabled: true
      }
    };
  }

  private getYAxes(): any {
    if (this.isCollection || _.isArray(this.resolvedSubObj)) {
      return {
        title: {text: null},
        min: 0,
        floor: 0,
        opposite: false,
        id: 0
      };
    }
    const standardFields: any[] = [];
    for (let i = 0; i < 100; i++) {
      const theColor = CHART_COLORS[i];
      const foundField = _.find(this.chartOptionsConfig.fields, (f: any) => f.yAxis === i) || {};
      standardFields.push({
        title: {text: null},
        min: 0,
        opposite: false,
        allowDecimals: foundField.yAxisOption === ADN_CHART_Y_AXIS_OPTIONS.shared ? true : !!foundField.allowDecimals,
        id: i,
        labels: {style: {color: theColor}}
      });
    }
    return standardFields;
  }

  private chunkYValue(yval: any): any {
    if (_.has(yval, 'amount')) {
      return yval.amount;
    } else if (_.has(yval, 'count')) {
      return yval.count;
    }
    return yval;
  }

  private getCollDataPoint(theChunks: any, objectId: string): any {
    let objParam = 'creative';
    if (this.resolvedChartType === 'LINE_ITEM') {
      objParam = 'lineItem';
    } else if (this.resolvedChartType === 'AD_UNIT') {
      objParam = 'adUnit';
    } else if (this.resolvedChartType === 'SEGMENT') {
      objParam = 'folder';
    } else if (this.resolvedChartType === 'EXTERNAL_AD_UNIT') {
      objParam = 'externalAdUnit';
    }

    return _.get(_.find(theChunks, (c: any) => {
      return c[objParam].id === objectId;
    }), (this.resolvedUseAlt ? (ALT_IDS[this.selFieldId] || this.selFieldId) : this.selFieldId)) || 0;
  }

  private startStatsGet(): void {
    if (!this.chartOptionsConfig) {
      return;
    }

    const requestVersion = ++this.requestVersion;
    const chunkSize = this.ChartOptionsUtil.getChunkSize(this.startDate, this.endDate, this.absChunkMin);
    const statsParams: Record<string, any> = {
      id: !this.isCollection ? this.obj.id : _.map(this.obj, 'id').join(';'),
      idKey: this.resolvedIdKey || this.chartOptionsConfig.idKey,
      startDate: this.startDate,
      endDate: this.endDate,
      timeout: 180000,
      groupBy: this.groupBy || chunkSize.key,
      currency: this.selectedCurrency || this.localUserProfile.getPreferredCurrency()
    };
    if (this.resolvedSubObj) {
      statsParams.aggregateTo = this.resolvedChartType;
    }
    if (!this.startDate) {
      delete statsParams.startDate;
      delete statsParams.endDate;
      statsParams.groupBy = 'AUTO_SCALE';
    }
    if (this.groupBy || this.isCollection) {
      statsParams.aggregateTo = this.groupBy ? chunkSize.key : this.resolvedChartType;

      const field = _.find(this.chartOptionsConfig.fields, ['id', this.selFieldId]);
      if (field) {
        _.forEach(this.series, (series: any) => {
          series.isPercentage = field.isPercentage;
          series.allowDecimals = field.allowDecimals;
          series.tooltip = field.tooltip;
        });
      }
    }

    this.statsSuccessCallback = (stats: any, theSeries?: any[]) => {
      this.previousStats = {
        statsParams,
        stats
      };
      applyHighchartsLocale(getIntlLocaleData(this.appLocale.getLocaleId()), this.networkTimezoneOffset);

      const theStats = this.groupById ? _.find(stats.chunks, (c: any) => {
        return _.get(c, ['folder', 'id']) === this.groupById;
      }) : stats;
      const theChunks = _.get(theStats, 'chunks');

      const nextTotals = _.get(theStats, 'totals') || stats;

      const extras = this.groupBy || this.isCollection || this.resolvedIdKey || _.indexOf(this.selFieldIds, 'customFields') < 0 ? [] : this.ChartOptionsUtil.getLineItemCustomFields(stats.totals);
      const yAxis = this.getYAxes();
      const updatingChart = !theSeries;

      const allSeries = theSeries || this.series;
      _.forEach(extras, (ex: string) => {
        const theAxis = _.find(yAxis, (axis: any) => axis.name === ex);
        allSeries.push({
          name: ex,
          id: ex,
          data: [],
          yAxis: theAxis ? (theAxis.id || 0) : 0,
          allowDecimals: false
        });
      });

      const keyedChunks = _.keyBy(theChunks, 'chunkStart');
      const colorCache: any[] = [];
      _.forEach(allSeries, (series: any, loopCounter: number) => {
        if (this.yAxisOption === ADN_CHART_Y_AXIS_OPTIONS.shared) {
          series.yAxis = 0;
        } else if (!this.isCollection && !this.resolvedIdKey) {
          series.yAxis = _.get(this.chartOptionsConfig.fields[series.id], 'yAxis') || series.yAxis;
        }

        const yAxisFieldId = _.get(this.chartOptionsConfig.fields[series.id], 'yAxis');
        const yAxisFieldColor = _.get(yAxis[yAxisFieldId], 'labels.style.color');
        series.color = this.isCollection || this.resolvedIdKey ? CHART_COLORS[loopCounter % CHART_COLORS.length] : yAxisFieldColor || CHART_COLORS[loopCounter % CHART_COLORS.length];
        if (colorCache.indexOf(series.color) > -1) {
          series.color = _.find(CHART_COLORS, (c: any) => c !== series.color);
        }
        colorCache.push(series.color);

        let runningTotal = 0;
        series.data = _.map(stats.chunkRange, (statsDate: any) => {
          let dataPoint = null;
          if (keyedChunks[statsDate]) {
            dataPoint = this.isCollection || this.resolvedIdKey
              ? this.getCollDataPoint(keyedChunks[statsDate].chunks, this.resolvedUseAlt ? (series.altId || series.id) : series.id)
              : keyedChunks[statsDate][this.resolvedUseAlt ? (series.altId || series.id) : series.id];
          }
          let value = this.chunkYValue(dataPoint) || 0;
          if (series.isPercentage) {
            value = value * 100;
            runningTotal = value;
          } else {
            runningTotal += value;
          }
          return {
            x: moment(statsDate).valueOf(),
            y: this.countType === ADN_CHART_COUNT_TYPES.cumulative && !series.singularOnly ? runningTotal : value
          };
        });
      });

      const noData = !this.series.length;
      // Defer template flags — StatsApiService / settle can overlap checkNoChanges (NG0100).
      deferSignalStateWrite(() => {
        if (this.destroyed || requestVersion !== this.requestVersion) {
          return;
        }
        this.totals.set(nextTotals);
        this.error.set(false);
        this.haveStats.set(true);
        this.noData.set(noData);
        this.isPending.set(false);
        if (updatingChart) {
          this.chartOptions = this.createChartConfig('line', this.series, yAxis, 280);
          this.renderChart();
        }
      });
    };

    if (_.isEqual(this.previousStats.statsParams, statsParams)) {
      this.statsSuccessCallback(this.previousStats.stats);
      return;
    }
    if (!_.includes(['hourly', 'half_hourly'], statsParams.groupBy.toLowerCase()) && statsParams.currency !== this.networkCurrency && !this.groupBy && statsParams.groupBy !== 'AUTO_SCALE') {
      statsParams.groupBy = 'DAILY';
    }
    if (this.showAudit && this.resolvedChartType === 'LINE_ITEM') {
      this.auditHook = this.auditHook || {};
      this.auditHook.objectType = _.get(ObjectTypeFinder.findByCaps(this.resolvedChartType), 'type');
      this.auditHook.startDate = statsParams.startDate;
      this.auditHook.endDate = statsParams.endDate;
      this.auditHook.chartMode = true;
      if (this.auditHook.triggerAuditQuery) {
        this.auditHook.triggerAuditQuery();
      }
    }
    deferSignalStateWrite(() => {
      if (!this.destroyed && requestVersion === this.requestVersion) {
        this.isPending.set(true);
      }
    });
    this.statsSubscription?.unsubscribe();
    this.statsSubscription = this.statsApi.get(statsParams).subscribe({next: (stats: any) => {
      this.statsSuccessCallback(stats);
    }, error: (data: any) => {
      if (!data.invalidDate) {
        this.adnLog.warn(data);
      }
      deferSignalStateWrite(() => {
        if (this.destroyed || requestVersion !== this.requestVersion) {
          return;
        }
        if (data.invalidDate) {
          this.error.set('errors.api.dateOption');
        } else if (data.status !== -1) {
          this.error.set('errors.api.stats');
        }
        this.isPending.set(false);
      });
    }});
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
